import { useEffect, useState } from "react";
import useEvent from "react-use-event-hook";

import { RideInvestigationApi } from "@ryde/shared";

import { getRetainedHistory } from "./investigation.history.utils";
import type { IRideInvestigationHistoryState } from "./investigation.models";

/**
 * Loads the History tab the first time it is opened for a ride and keeps the result while the panel stays on
 * that ride, so switching tabs never refetches; `refresh` asks again on demand. Lives in the panel (which
 * outlives its tabs) for that reason. A different ride, or a closed panel (no rideId), drops the result and
 * starts over.
 */
export const useRideInvestigationHistory = ({
  rideId,
  isEnabled,
}: {
  rideId?: number;
  isEnabled: boolean;
}) => {
  const [requestedRideId, setRequestedRideId] = useState<number | undefined>(
    undefined,
  );
  // Bumped by `refresh`: the same ride is requested again.
  const [reloadCount, setReloadCount] = useState(0);
  const [state, setState] = useState<IRideInvestigationHistoryState>({
    isLoading: false,
    hasError: false,
  });

  // Latch the request: it is made once per ride and survives the tab being switched away.
  useEffect(() => {
    if (requestedRideId !== undefined && requestedRideId !== rideId) {
      setRequestedRideId(undefined);

      return;
    }

    if (isEnabled && rideId && requestedRideId === undefined) {
      setRequestedRideId(rideId);
    }
  }, [isEnabled, rideId, requestedRideId]);

  useEffect(() => {
    if (!requestedRideId) {
      setState({ isLoading: false, hasError: false });

      return;
    }

    const abortController = new AbortController();
    // Per-run flag: only this run's response may write state, never an older ride's late answer.
    let isCurrentRun = true;
    // A refresh keeps the rows already on screen while it runs and when it fails.
    const setKeepingRows = (
      status: Pick<IRideInvestigationHistoryState, "isLoading" | "hasError">,
    ): void =>
      setState(previous => ({
        rideId: requestedRideId,
        history: getRetainedHistory(previous, requestedRideId),
        ...status,
      }));

    setKeepingRows({ isLoading: true, hasError: false });

    const loadHistory = async (): Promise<void> => {
      try {
        const result = await RideInvestigationApi.fetchHistory(
          requestedRideId,
          abortController,
        );

        if (!isCurrentRun) {
          return;
        }

        if (result?.success && result.data) {
          setState({
            rideId: requestedRideId,
            history: result.data,
            isLoading: false,
            hasError: false,
          });
        } else {
          setKeepingRows({ isLoading: false, hasError: true });
        }
      } catch {
        if (isCurrentRun) {
          setKeepingRows({ isLoading: false, hasError: true });
        }
      }
    };

    void loadHistory();

    return () => {
      isCurrentRun = false;
      abortController.abort();
    };
  }, [requestedRideId, reloadCount]);

  const refresh = useEvent(() => setReloadCount(count => count + 1));
  const isCurrentRide = state.rideId !== undefined && state.rideId === rideId;

  return {
    history: isCurrentRide ? state.history : undefined,
    // Between enabling the tab and the request starting there is one render with no state yet — count it as loading.
    isLoading: isCurrentRide ? state.isLoading : isEnabled && !!rideId,
    hasError: isCurrentRide && state.hasError,
    refresh,
  };
};
